"use client";

import Link from "next/link";
import Button from "../../ui/Button";
import Image from "next/image";
import BenefitCard from "@/components/ui/BenefitCard";
import DeliveryTruckIcon from "@/components/icons/DeliveryTruckIcon";
import GiftsIcon from "@/components/icons/GiftsIcon";
import HeadsetIcon from "@/components/icons/HeadsetIcon";
import ShopAllIcon from "@/components/icons/ShopAllIcon";
import StorefrontIcon from "@/components/icons/StorefrontIcon";
import TagIcon from "@/components/icons/TagIcon";

const BENEFITS = [
  {
    title: "Largest Selection",
    description:
      "An expertly curated menu featuring the best cannabis brands and premium products",
    icon: <ShopAllIcon size={22} className="h-5 w-5 text-blue-80" />,
  },
  {
    title: "Retail Experience",
    description:
      "A simple, stress-free ordering process online or in-store",
    icon: <StorefrontIcon size={22} className="h-5 w-5 text-blue-80" />,
  },
  {
    title: "Customer Service",
    description:
      "Our team knows their stuff and is happy to help, in-store or online",
    icon: <HeadsetIcon size={22} className="h-5 w-5 text-blue-80" />,
  },
  {
    title: "Fair Pricing",
    description:
      "High-quality cannabis at competitive prices across every budget range",
    icon: <TagIcon size={22} className="h-5 w-5 text-blue-80" />,
  },
  {
    title: "Dank Club",
    description:
      "Earn points on every purchase and swap them for exclusive rewards and discounts",
    icon: <GiftsIcon size={22} className="h-5 w-5 text-blue-80" />,
  },
  {
    title: "Convenience",
    description:
      "Shop in-store, click & collect, or enjoy fast delivery across Alberta",
    icon: <DeliveryTruckIcon size={22} className="h-5 w-5 text-blue-80" />,
  },
] as const;

export default function LocationFinestSection() {
  return (
    <section className="w-full max-w-none" aria-labelledby="location-finest-heading">
      <div className="w-full">
        <h2
          id="location-finest-heading"
          className="text-h3-mobile font-bold text-text-primary sm:text-h3-desktop"
        >
          Dank&apos;s finest
        </h2>

        <div className="mt-4 overflow-x-auto scrollbar-hide md:hidden">
          <div className="grid w-max auto-cols-[282px] grid-flow-col grid-rows-2 gap-2">
            {BENEFITS.map((benefit) => (
              <div key={benefit.title} className="w-[282px]">
                <BenefitCard
                  icon={benefit.icon}
                  title={benefit.title}
                  description={benefit.description}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="mt-4 hidden grid-cols-1 gap-2 md:grid md:grid-cols-2 lg:grid-cols-3 lg:gap-4">
          {BENEFITS.map((benefit) => (
            <BenefitCard
              key={benefit.title}
              icon={benefit.icon}
              title={benefit.title}
              description={benefit.description}
            />
          ))}
        </div>

        <div className="relative mt-4 w-full overflow-hidden rounded-2xl bg-[#FFD2D2]">
          <div className="absolute inset-0">
            <Image
              src="/media/sections/lifestyle/backgrounds/banner-background-shape.webp"
              alt=""
              fill
              className="object-cover"
              aria-hidden="true"
            />
          </div>

          <div className="relative z-10 flex flex-col items-center justify-between gap-4 px-4 pb-4 sm:px-6 sm:pb-6 lg:flex-row">
            <div className="flex h-48 w-full items-center justify-center overflow-hidden sm:hidden">
              <Image
                src="/media/sections/lifestyle/backgrounds/banner-image.webp"
                alt=""
                width={220}
                height={300}
                className="w-full object-center"
                aria-hidden="true"
              />
            </div>
            <div className="-mb-8 hidden h-32 overflow-hidden sm:block">
              <Image
                src="/media/sections/lifestyle/backgrounds/banner-image.webp"
                alt=""
                width={220}
                height={300}
                className="-mt-6 object-top"
                aria-hidden="true"
              />
            </div>

            <div className="flex-1 sm:mt-7">
              <h3 className="mb-1 text-body-20 font-bold leading-tight text-text-primary">
                Enjoy a high country life
              </h3>
              <p className="text-body-16 font-medium leading-relaxed text-text-secondary">
                Gummies, chocolates and drinks from Canada&apos;s best makers, ready for pickup or same day delivery.
              </p>
            </div>

            <Button asChild variant="primary" size="lg" className="w-full sm:w-fit px-6 py-3 rounded-xl mt-6">
              <Link href="/search?category=Edibles">
                Shop our new edibles
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
